// typescript
import React from "react";
import { cn } from "@/lib/utils";

// Shared Reddit/Facebook-style floating label, used by both InputWithCharCounter and
// TextAreaWithCharCounter so the two stay visually identical. The label rests inside the
// empty control like a placeholder and floats to a small label on focus or once filled.
// Detection relies on the control carrying a single-space placeholder (`placeholder=" "`)
// plus the `peer` class, so `peer-placeholder-shown` matches the empty state.
// `right-3 truncate` is the SINGLE-LINE CONTRACT (`ACCOUNT-ACCESS-14`). The label is absolutely
// positioned with nothing bounding it on the right, and at rest it renders at 16px; the control it
// sits in is a fixed `h-12`. So a catalog value long enough to wrap did not grow the field, it filled
// it: the Spanish reset-account label is 42 characters against English's 24 and at 412 CSS px it
// wrapped to two centred lines, edge to edge, turning the one empty input on the recovery screen into
// the densest block of text on the card. `es` is simply the first locale over the threshold (`el` 33,
// `ro`/`it` 32 behind it), so the constraint belongs here, in the component, rather than in a string.
// The full text stays in the DOM — this is a real `<label htmlFor>` and its accessible name is
// unaffected; only the painted line is clipped. A taller field is the WRONG answer: the floated state
// at `text-xs` already fits the longest value on one line.
// `text-left` (`ACCOUNT-ACCESS-29`, A10): the label parks above where its own value begins, so it
// declares the start edge instead of inheriting a centred host's `text-center`.
const FLOATING_LABEL_BASE = cn(
    "pointer-events-none absolute left-3 right-3 z-10 origin-left truncate text-left text-muted-foreground transition-all duration-150",
);

// Single-line: rests vertically centered (placeholder-like) and floats to the top padding.
// Tailwind emits `focus` after `placeholder-shown`, so focus wins when the field is
// focused-but-empty.
export const FLOATING_INPUT_LABEL_CLASS = cn(
    FLOATING_LABEL_BASE,
    "top-1.5 text-xs", // floated (default: field has a value)
    "peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base", // resting
    "peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-primary", // active
);

// Multi-line: a textarea's resting "placeholder" sits at the first line (top-left), not the
// vertical center, so it rests near the top and floats a little higher.
export const FLOATING_TEXTAREA_LABEL_CLASS = cn(
    FLOATING_LABEL_BASE,
    "top-1.5 text-xs", // floated
    "peer-placeholder-shown:top-3 peer-placeholder-shown:text-base", // resting (first text line)
    "peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-primary", // active
);

type Props = {
    /** Must match the control's `id` so the label's `htmlFor` resolves (accessible name). */
    htmlFor?: string;
    className?: string;
    /** Picks the resting position: centered for inputs, top-aligned for textareas. */
    variant?: "input" | "textarea";
    children: React.ReactNode;
};

export const FloatingLabel: React.FC<Props> = ({ htmlFor, className, variant = "input", children }) => (
    <label
        htmlFor={htmlFor}
        className={cn(
            variant === "textarea" ? FLOATING_TEXTAREA_LABEL_CLASS : FLOATING_INPUT_LABEL_CLASS,
            className,
        )}
    >
        {children}
    </label>
);

export default FloatingLabel;
